import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Palette, Image, ShieldCheck, ArrowRight, Settings } from "lucide-react";

export default function SaasWhiteLabel() {
  const [domainType, setDomainType] = useState("subdomain"); // subdomain vs custom
  const [selectedColor, setSelectedColor] = useState("#7C3AED"); // purple vs others

  const brandingColors = [
    { value: "#7C3AED", name: "Purple Gradient" },
    { value: "#3B82F6", name: "Classic Blue" },
    { value: "#06B6D4", name: "Ocean Cyan" },
    { value: "#10B981", name: "Forest Green" }
  ];

  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-violet-600/10 rounded-full blur-[100px]" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-600/10 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column: Text Features */}
          <div className="lg:col-span-5 space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-300 rounded-full text-sm font-bold border border-violet-500/30">
              <Globe size={16} />
              <span>100% White-Labeled</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight">
              Launch Your Own Branded LMS
            </h2>
            
            <p className="text-slate-400 text-base leading-relaxed font-semibold">
              Remove all Skillzy branding. Serve your teachers and students under your own logo, custom colors, assets, and root domain to build your institutional authority.
            </p>

            {/* Custom domain transition helper buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setDomainType("subdomain")}
                className={`flex-1 py-3 px-4 rounded-xl text-xs font-black border transition-all ${
                  domainType === "subdomain"
                    ? "bg-slate-800 text-white border-slate-700 shadow-md"
                    : "bg-transparent text-slate-400 border-slate-800 hover:text-white"
                }`}
              >
                1. Standard Subdomain
              </button>
              <button
                onClick={() => setDomainType("custom")}
                className={`flex-1 py-3 px-4 rounded-xl text-xs font-black border transition-all ${
                  domainType === "custom"
                    ? "bg-violet-600 text-white border-violet-500 shadow-md"
                    : "bg-transparent text-slate-400 border-slate-800 hover:text-white"
                }`}
              >
                2. Enterprise Custom Domain
              </button>
            </div>

            {/* Features Bullet List */}
            <div className="space-y-4 pt-4 border-t border-slate-800">
              <div className="flex items-center gap-3">
                <ShieldCheck size={20} className="text-violet-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">SSL Certificate Issued Automatically</span>
              </div>
              <div className="flex items-center gap-3">
                <Palette size={20} className="text-blue-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">Custom System-wide Themes & Fonts</span>
              </div>
              <div className="flex items-center gap-3">
                <Image size={20} className="text-cyan-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">No Skillzy branding in student PDFs or APKs</span>
              </div>
            </div>
          </div>

          {/* Right Column: Customizer Canvas Mockup */}
          <div className="lg:col-span-7">
            <div className="bg-slate-950/80 rounded-[2.5rem] border border-slate-800 p-8 shadow-2xl relative overflow-hidden flex flex-col justify-between min-h-[460px]">
              
              {/* Browser bar */}
              <div className="flex items-center gap-3 border-b border-slate-850 pb-4 mb-8">
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                </div>
                
                {/* Simulated address bar */}
                <div className="flex-1 bg-slate-900 rounded-lg py-1 px-4 text-[10px] font-mono text-slate-400 flex items-center gap-2 border border-slate-800 select-none">
                  <Globe size={10} className="text-slate-500" />
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={domainType}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.3 }}
                      className="font-bold text-slate-200"
                    >
                      {domainType === "subdomain" ? "https://abc.skillzy.pro" : "https://lms.yourschool.com"}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>

              {/* Designer controls mockup */}
              <div className="flex-1 grid md:grid-cols-12 gap-8 items-center">
                
                {/* Control Panel (left) */}
                <div className="md:col-span-5 space-y-5 bg-slate-900/60 p-5 rounded-2xl border border-slate-800">
                  <div className="flex items-center gap-2 border-b border-slate-850 pb-2">
                    <Settings size={14} className="text-violet-400" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Settings</span>
                  </div>
                  
                  {/* Select Theme Color */}
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Theme color</label>
                    <div className="flex gap-2">
                      {brandingColors.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => setSelectedColor(color.value)}
                          className={`w-6 h-6 rounded-full transition-transform ${
                            selectedColor === color.value ? "scale-125 ring-2 ring-violet-500 ring-offset-2 ring-offset-slate-950" : ""
                          }`}
                          style={{ backgroundColor: color.value }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Logo upload mockup */}
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Header logo</label>
                    <div className="border border-dashed border-slate-800 rounded-lg p-2.5 text-center text-[9px] text-slate-500 font-bold hover:border-slate-700 transition-colors">
                      school-crest-white.png
                    </div>
                  </div>
                </div>

                {/* Instant Live Preview (right) */}
                <div className="md:col-span-7 bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-100 dark:border-slate-850 shadow-lg text-slate-850 flex flex-col justify-between aspect-video relative overflow-hidden">
                  
                  {/* Glowing custom color block background */}
                  <div 
                    className="absolute inset-x-0 top-0 h-1 transition-all duration-500" 
                    style={{ backgroundColor: selectedColor }}
                  />

                  {/* Header preview */}
                  <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-md flex items-center justify-center text-white" style={{ backgroundColor: selectedColor }}>
                        🎓
                      </div>
                      <span className="text-[9px] font-black text-slate-800 dark:text-white">XYZ Academy</span>
                    </div>
                    <div className="flex gap-2 text-[7px] text-slate-500 font-bold uppercase">
                      <span>Exams</span>
                      <span>Classes</span>
                    </div>
                  </div>

                  {/* Body Preview */}
                  <div className="my-4 text-center">
                    <p className="text-[10px] font-black text-slate-800 dark:text-white">Launch Smart Portal</p>
                    <p className="text-[7px] text-slate-500 mt-1 max-w-[150px] mx-auto">Access digital curriculum, exams, and classes.</p>
                  </div>

                  {/* CTA Preview */}
                  <div className="flex justify-center">
                    <button
                      className="px-4 py-1.5 text-[8px] font-black text-white rounded-md shadow-sm transition-all"
                      style={{ backgroundColor: selectedColor }}
                    >
                      Log in to Academy
                    </button>
                  </div>

                </div>

              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
